import { readonly } from 'vue'

import { IncidentId } from '@collector/sportsapi-client-legacy'
import IncidentDeleteModal from '@mobile/views/Relation/DetailsPanel/Modals/IncidentDeleteModal.vue'
import IncidentDetailsModal from '@mobile/views/Relation/DetailsPanel/Modals/IncidentDetailsModal/IncidentDetailsModal.vue'
import SwapIncidentModal from '@mobile/views/Relation/DetailsPanel/Modals/SwapIncidentModal/SwapIncidentModal.vue'
import {
  isDetailsComponentConfiguration,
  useSportOptions,
} from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'
import { useHistoryIncidents } from '@mobile/views/Relation/DetailsPanel/Table/useHistoryIncidents'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import IncidentAddModal from '../Modals/IncidentAddModal.vue'
import VIPIncidentTimeModal from '../Modals/VIPIncidentTimeModal/VIPIncidentTimeModal.vue'
import { useModals } from './useModals'

type UseIncidentModalsReturn = {
  currentModal: ReturnType<typeof useModals>['currentModal']
  isOpen: ReturnType<typeof useModals>['isOpen']
  openIncidentAddModal: () => void
  openIncidentsDetailModal: (
    linkedIncident: ProbableLinkedIncident,
    shouldSelectIncident?: boolean,
  ) => void
  openSwapIncidentModal: (
    linkedIncident: ProbableLinkedIncident,
    shouldSelectIncident?: boolean,
  ) => void
  openIncidentsDeleteModal: (
    linkedIncident: ProbableLinkedIncident,
    shouldSelectIncident?: boolean,
  ) => void
  openVIPIncidentTimeModal: (
    incidentId: IncidentId,
    participantId: number,
  ) => void
}

export function useIncidentModals(): UseIncidentModalsReturn {
  const eventInjections = useEventInjections()
  const { sport } = eventInjections
  const { detailsConfiguration } = useSportOptions(sport)
  const { selectIncident } = useHistoryIncidents(eventInjections)
  const { currentModal, openModal, isOpen } = useModals()

  function openIncidentAddModal(): void {
    openModal(IncidentAddModal)
  }

  function openIncidentsDetailModal(
    linkedIncident: ProbableLinkedIncident,
    shouldSelectIncident = true,
  ): void {
    const incidentDetailsConfiguration =
      detailsConfiguration[linkedIncident.incident.incident_id]

    if (!incidentDetailsConfiguration) {
      throw new Error(
        `No details configuration found for incident ID: ${linkedIncident.incident.incident_id}`,
      )
    }

    const IncidentDetailsModalComponent = isDetailsComponentConfiguration(
      incidentDetailsConfiguration,
    )
      ? incidentDetailsConfiguration.component
      : IncidentDetailsModal

    openModal(IncidentDetailsModalComponent, {
      linkedIncident,
    })

    if (shouldSelectIncident) {
      selectIncident(linkedIncident)
    }
  }

  function openSwapIncidentModal(
    linkedIncident: ProbableLinkedIncident,
    shouldSelectIncident = true,
  ): void {
    openModal(SwapIncidentModal, {
      linkedIncident: readonly(linkedIncident),
    })

    if (shouldSelectIncident) {
      selectIncident(linkedIncident)
    }
  }

  function openIncidentsDeleteModal(
    linkedIncident: ProbableLinkedIncident,
    shouldSelectIncident = true,
  ): void {
    openModal(IncidentDeleteModal, {
      linkedIncident: readonly(linkedIncident),
    })

    if (shouldSelectIncident) {
      selectIncident(linkedIncident)
    }
  }

  function openVIPIncidentTimeModal(
    incidentId: IncidentId,
    participantId: number,
  ): void {
    openModal(VIPIncidentTimeModal, { incidentId, participantId })
  }

  return {
    currentModal,
    isOpen,
    openIncidentAddModal,
    openIncidentsDetailModal,
    openSwapIncidentModal,
    openIncidentsDeleteModal,
    openVIPIncidentTimeModal,
  }
}
